import React, { useState, useEffect } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import ProductNavbar from "./ProductNavbar";

function ProductAttribute() {
  const { uuid } = useParams();
  const [attributeGroups, setAttributeGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Fetch attributes for the product
  useEffect(() => {
    if (!uuid) return;

    const fetchAttributes = async () => {
      try {
        console.log(`📡 Fetching attributes for UUID: ${uuid}`);
        const res = await axios.get(`${baseurl}getVendorProductAttributesByUUID/${uuid}`);

        console.log("📥 Received data:", res.data);

        if (res.data?.data?.attributes) {
          const attrData = res.data.data.attributes;

          // Convert JSON object -> editable array
          const groups = Object.entries(attrData).map(([groupName, kvPairs]) => ({
            groupName,
            items: Object.entries(kvPairs).map(([key, value]) => ({ key, value })),
          }));

          setAttributeGroups(groups.length > 0 ? groups : [{ groupName: "", items: [{ key: "", value: "" }] }]);
        } else {
          // No data yet — start empty
          setAttributeGroups([{ groupName: "", items: [{ key: "", value: "" }] }]);
        }
      } catch (err) {
        console.error("❌ Failed to fetch product attributes:", err);
        Swal.fire({
          icon: "error",
          title: "Failed to fetch product attributes!",
          timer: 2000,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
        });
        setAttributeGroups([{ groupName: "", items: [{ key: "", value: "" }] }]);
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, [uuid]);

  // ✅ Handlers
  const handleItemChange = (groupIndex, itemIndex, field, value) => {
    const groups = [...attributeGroups];
    groups[groupIndex].items[itemIndex][field] = value;
    setAttributeGroups(groups);
  };

  const addItem = (groupIndex) => {
    const groups = [...attributeGroups];
    groups[groupIndex].items.push({ key: "", value: "" });
    setAttributeGroups(groups);
  };

  const removeItem = (groupIndex, itemIndex) => {
    const groups = [...attributeGroups];
    groups[groupIndex].items = groups[groupIndex].items.filter((_, i) => i !== itemIndex);
    if (groups[groupIndex].items.length === 0) {
      groups[groupIndex].items.push({ key: "", value: "" });
    }
    setAttributeGroups(groups);
  };

  const addGroup = () => {
    setAttributeGroups([...attributeGroups, { groupName: "", items: [{ key: "", value: "" }] }]);
  };

  const removeGroup = (groupIndex) => {
    const groups = attributeGroups.filter((_, i) => i !== groupIndex);
    setAttributeGroups(groups.length > 0 ? groups : [{ groupName: "", items: [{ key: "", value: "" }] }]);
  };

  // ✅ Submit handler (add/update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Convert grouped array → object
      const payload = {};
      attributeGroups.forEach((group) => {
        if (group.groupName.trim()) {
          payload[group.groupName.trim()] = {};
          group.items.forEach((item) => {
            if (item.key.trim()) payload[group.groupName.trim()][item.key.trim()] = item.value;
          });
        }
      });

      console.log("🛰️ Sending payload to backend:", payload);

      await axios.put(`${baseurl}updateVendorProductAttributes/${uuid}`, { attributes: payload });

      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Product attributes saved successfully!",
        showConfirmButton: false,
        timer: 2000,
        toast: true,
      });
    } catch (error) {
      console.error("❌ Error updating product attributes:", error);
      Swal.fire({
        position: "top-end",
        icon: "error",
        title: "Failed to update product attributes!",
        showConfirmButton: false,
        timer: 2500,
        toast: true,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-4 text-center">Loading product attributes...</div>;

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">
          <ProductNavbar />

          <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
            <div className="breadcrumb-title pe-3">Manage Product Attributes</div>
          </div>

          <div className="col-12 mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-primary text-white">
                Edit Product Attributes
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  {attributeGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className="mb-4 border p-3 rounded">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <input
                          type="text"
                          className="form-control w-50"
                          placeholder="Group Name"
                          value={group.groupName}
                          onChange={(e) => {
                            const groups = [...attributeGroups];
                            groups[groupIndex].groupName = e.target.value;
                            setAttributeGroups(groups);
                          }}
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={() => removeGroup(groupIndex)}
                        >
                          Remove Group
                        </button>
                      </div>

                      {group.items.map((item, itemIndex) => (
                        <div className="row mb-2 align-items-start" key={itemIndex}>
                          <div className="col-md-4 mb-2">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Key"
                              value={item.key}
                              onChange={(e) =>
                                handleItemChange(groupIndex, itemIndex, "key", e.target.value)
                              }
                              required
                            />
                          </div>
                          <div className="col-md-6 mb-2">
                            <textarea
                              className="form-control"
                              placeholder="Value"
                              rows={2}
                              value={item.value}
                              onChange={(e) =>
                                handleItemChange(groupIndex, itemIndex, "value", e.target.value)
                              }
                              required
                            />
                          </div>
                          <div className="col-md-2 mb-2">
                            <button
                              type="button"
                              className="btn btn-danger w-100"
                              onClick={() => removeItem(groupIndex, itemIndex)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-secondary mb-2"
                        onClick={() => addItem(groupIndex)}
                      >
                        Add Attribute
                      </button>
                    </div>
                  ))}

                  <button type="button" className="btn btn-primary mb-3" onClick={addGroup}>
                    Add Group
                  </button>

                  <div className="col-12 text-end">
                    <button type="submit" className="btn btn-success">
                      Save Attributes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default ProductAttribute;
